import useAuthStore from "@/store/useAuthStore";
import { getGeoCodeFromAddress, parsePhone, trimPhoneNumber } from "@/utils/helpers";
import { Badge, Box, Button, Card, CloseButton, FileButton, Flex, Group, Modal, Select, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconFileUpload } from "@tabler/icons-react";
import axios from "axios";
import { saveAs } from "file-saver";
import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { read, utils } from "xlsx";

export default function ExcelUploadModal({ mutate }) {
  const [opened, { open, close }] = useDisclosure();
  const user = useAuthStore((state) => state.user);
  const [type, setType] = useState("unload");

  const [list, setList] = useState([]);
  const [company, setCompany] = useState("");
  const resetRef = useRef(null);

  function handleTemplateDownload() {
    saveAs("/partners.xlsx", "거래처_등록양식.xlsx");
    toast.success("엑셀 양식 다운로드 완료");
  }

  async function handleFile(file) {
    console.log("🚀 ~ file: ExcelUploadModal.jsx:17 ~ handleFile ~ file:", file);
    const buff = await file.arrayBuffer();
    const wb = await read(buff);

    const data = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
    setList(data);
    resetRef.current?.();
    open();
  }

  function handleDelete(name) {
    setList(list.filter((item) => item["거래처명"] !== name));
  }

  function handleClose() {
    setList([]);
    close();
  }

  async function handleUpload() {
    // list 중에 lat가 없는 것이 하나라도 있는 경우 에러
    if (list.some((item) => !item.lat))
      return toast.error(
        "주소 검증이 필요합니다. 잘못된 주소는 수정 후 다시 등록하거나 이후 거래처 관리에서 직접 수정해주세요"
      );

    // list에 중복된 거래처명이 있는 경우 에러
    // if (list.some((item) => list.filter((i) => i["거래처명"] === item["거래처명"]).length > 1)) {
    //   toast.error("거래처명이 중복된 건이 있습니다. 일괄 등록 후 직접 확인해주세요");
    // }

    const processedData = await Promise.all(
      list.map(async (item) => {
        // Perform asynchronous operation on each item

        //거래처명	거래처주소	담당자명	전화번호
        const processedItem = await axios({
          url: "/partners",
          method: "POST",
          data: {
            data: {
              name: item["거래처명"],
              address: item["거래처주소"],
              lat: item.lat,
              lng: item.lng,
              managerName: item["담당자명"],
              managerPhoneNumber: trimPhoneNumber(item["전화번호"]),
              type: type,
              company: user?.company?.id,
            },
          },
        });
        return processedItem;
      })
    );

    toast.success("등록 완료");
    mutate();
    handleClose();
  }

  const clearFile = () => {
    resetRef.current?.();
  };

  async function handleAddGeoCode() {
    const processedData = [];
    const _loading = toast.loading("주소 검증 중입니다");
    try {
      for await (const item of list) {
        const res = await getGeoCodeFromAddress(item["거래처주소"]);
        processedData.push({
          ...item,
          거래처주소: res?.address || item["거래처주소"],
          lat: res?.lat,
          lng: res?.lng,
        });
      }

      setList(processedData);
      toast.success("주소 검증 완료", { id: _loading });
    } catch (error) {
      toast.error("주소 검증 중 오류 발생", { id: _loading });
      console.error(error);
    }
  }

  return (
    <>
      <Flex gap="xs">
        <Button color="excel" leftIcon={<IconDownload size={18} />} onClick={handleTemplateDownload}>
          양식 다운로드
        </Button>
        <FileButton
          onChange={handleFile}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          resetRef={resetRef}
        >
          {(props) => (
            <Button
              onClick={(event) => (event.target.value = null)}
              {...props}
              color="excel"
              leftIcon={<IconFileUpload size={18} />}
            >
              엑셀 일괄 등록
            </Button>
          )}
        </FileButton>
      </Flex>

      <Modal opened={opened} onClose={handleClose} size="xl" title="상하차지 목록">
        <Select
          label="상/하차지 유형"
          required
          placeholder="상/하차지 유형을 선택해주세요"
          value={type}
          onChange={setType}
          data={[
            { value: "load", label: "상차지" },
            { value: "unload", label: "하차지" },
          ]}
          mb="sm"
        />
        {list.map((item, idx) => (
          <Card key={idx} p="xs" shadow={0} mb="xs" bg={!item["거래처주소"] && "red.2"}>
            <Group>
              <Box>
                <Flex gap="xs" align="baseline">
                  <Text fw={700}>{item["거래처명"]}</Text>
                  <Text size="sm" color="dimmed">
                    담당자 정보: {item["담당자명"]} {parsePhone(item["전화번호"])}
                  </Text>
                </Flex>
                {item["거래처주소"] ? (
                  <Text size="sm">{item["거래처주소"]}</Text>
                ) : (
                  <Text size="sm" fw={700}>
                    주소 검색/검증 불가
                  </Text>
                )}
              </Box>

              <Flex align="center" gap="xs">
                <Badge color={item.lat ? "teal" : "red"} variant="outline">
                  {item.lat ? "주소 검증 완료" : "주소 검증 필요"}
                </Badge>
                <CloseButton color="red" variant="outline" onClick={() => handleDelete(item["거래처명"])} />
              </Flex>
            </Group>
          </Card>
        ))}

        <Flex justify="flex-end" gap="sm">
          <Button size="md" variant="outline" onClick={handleAddGeoCode}>
            주소 검증
          </Button>
          <Button size="md" onClick={handleUpload}>
            등록하기
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
